import type { ReactNode } from "react";
import {
  Group,
  NumberFormatter,
  Stack,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { REPLAY_BLOCK_CLASS } from "../../../utils/statsig";
import type { PensionGroup } from "./pensionSegments";

export interface PensionFiguresProps {
  variant: "full" | "publicOnly";
  totalCapital: number | null;
  retirementAge: number;
  expectedMonthlyPension: number | null;
  guaranteedMonthlyPension: number | null;
  affectedByRiskPercent: number | null;
  riskLegendGroups: PensionGroup[];
  expectedFeesUntilRetirement: number | null;
  footnote: { answered: number; total: number } | null;
  forecastIsEstimate: boolean;
  onForecastInfo: () => void;
  /** The missing income is what withholds the forecast. */
  missingIncome?: boolean;
  onAddIncome: () => void;
  publicComponents?: {
    premiepension: number | null;
    inkomstpension: number | null;
    garantipension: number | null;
  } | null;
}

const RISK_DOT_COLOUR: Record<PensionGroup, string> = {
  private: "#7ab6ff",
  occupational: "var(--wallet-color-primary-shade-300)",
  public: "#c8ebd2",
};

const RISK_DOT_MESSAGES = defineMessages({
  private: {
    id: "app.poaOverview.figures.riskLegend.private",
    defaultMessage: "privat pension",
  },
  occupational: {
    id: "app.poaOverview.figures.riskLegend.occupational",
    defaultMessage: "tjänstepension",
  },
  public: {
    id: "app.poaOverview.figures.riskLegend.public",
    defaultMessage: "allmän pension",
  },
});

const hatched = (colour: string) =>
  "repeating-linear-gradient(45deg, " +
  colour +
  ", " +
  colour +
  " 2px, #ffffff 2px, #ffffff 4px)";

function Figure({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack gap={2}>
      <Text size="sm" c="var(--wallet-color-neutrals-gray-600)">
        {label}
      </Text>
      {children}
    </Stack>
  );
}

function Value({ children }: { children: ReactNode }) {
  return (
    <Text
      fw={700}
      fz="h4"
      lh="h4"
      c="var(--wallet-color-neutrals-black)"
      // Every figure here is this customer's own pension money, so none of it belongs
      // in a session replay.
      className={REPLAY_BLOCK_CLASS}
    >
      {children}
    </Text>
  );
}

// PEN-358: the figure sits inside the button, so tapping the value works as well as the icon.
function ForecastInfoButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <UnstyledButton onClick={onClick} w="fit-content">
      <Group gap={6} wrap="nowrap">
        {children}
        <IconInfoCircle size={16} aria-hidden="true" />
        {/* Hidden text rather than aria-label, which would drop the value from the name. */}
        <VisuallyHidden>
          <FormattedMessage
            id="app.poaOverview.foreCast.headline"
            defaultMessage="Så har vi räknat prognosen"
          />
        </VisuallyHidden>
      </Group>
    </UnstyledButton>
  );
}

function Sek({ value, approx }: { value: number; approx?: boolean }) {
  return (
    <NumberFormatter
      value={value}
      thousandSeparator=" "
      decimalScale={0}
      prefix={approx ? "ca " : undefined}
      suffix=" SEK"
    />
  );
}

function PublicOnlyFigures({
  totalCapital,
  publicComponents,
}: Pick<PensionFiguresProps, "totalCapital" | "publicComponents">) {
  const parts: Array<[string, ReactNode, number | null]> = [
    [
      "premiepension",
      <FormattedMessage
        key="premiepension"
        id="pensionOverview.summaryCard.premiumPension"
        defaultMessage="Premiepension"
      />,
      publicComponents?.premiepension ?? null,
    ],
    [
      "inkomstpension",
      <FormattedMessage
        key="inkomstpension"
        id="pensionOverview.summaryCard.incomePension"
        defaultMessage="Inkomstpension"
      />,
      publicComponents?.inkomstpension ?? null,
    ],
    [
      "garantipension",
      <FormattedMessage
        key="garantipension"
        id="pensionOverview.summaryCard.guaranteePension"
        defaultMessage="Garantipension"
      />,
      publicComponents?.garantipension ?? null,
    ],
  ];

  return (
    <Stack gap="lg">
      {totalCapital !== null && (
        <Figure
          label={
            <FormattedMessage
              id="pensionOverview.summaryCard.SummarystatePension"
              defaultMessage="Total allmän pension"
            />
          }
        >
          <Value>
            <Sek value={totalCapital} />
          </Value>
        </Figure>
      )}
      {parts.map(([key, label, value]) =>
        value === null ? null : (
          <Figure key={key} label={label}>
            <Value>
              <Sek value={value} />
            </Value>
          </Figure>
        ),
      )}
    </Stack>
  );
}

export function PensionFigures({
  variant,
  totalCapital,
  retirementAge,
  expectedMonthlyPension,
  guaranteedMonthlyPension,
  affectedByRiskPercent,
  riskLegendGroups,
  expectedFeesUntilRetirement,
  footnote,
  forecastIsEstimate,
  onForecastInfo,
  missingIncome = false,
  onAddIncome,
  publicComponents,
}: PensionFiguresProps) {
  const intl = useIntl();
  // The footnote explains the asterisk, so both hang off there being a figure to mark.
  const showAsterisk =
    forecastIsEstimate && expectedMonthlyPension !== null && !missingIncome;
  // Only a schablon needs the explanation; a missing income gets the add income link instead.
  const withForecastInfo = (figure: ReactNode) =>
    forecastIsEstimate ? (
      <ForecastInfoButton onClick={onForecastInfo}>{figure}</ForecastInfoButton>
    ) : (
      figure
    );

  if (variant === "publicOnly") {
    return (
      <PublicOnlyFigures
        totalCapital={totalCapital}
        publicComponents={publicComponents}
      />
    );
  }

  return (
    <Stack gap="lg">
      {totalCapital !== null && (
        <Figure
          label={
            <FormattedMessage
              id="pensionOverview.summaryCard.totalPension"
              defaultMessage="Totalt pensionskapital"
            />
          }
        >
          <Value>
            <Sek value={totalCapital} />
          </Value>
        </Figure>
      )}

      <Figure
        label={
          <FormattedMessage
            id="app.poaOverview.figures.expectedPension"
            defaultMessage="Förväntad pension vid {age} år"
            values={{ age: retirementAge }}
          />
        }
      >
        {/* The sheet behind this is the only place that explains a schablon forecast. */}
        {missingIncome ? (
          <UnstyledButton onClick={onAddIncome} w="fit-content">
            <Group gap={4} wrap="nowrap">
              <PlusIcon width={14} height={14} aria-hidden="true" />
              <Text size="sm" fw={700} td="underline">
                <FormattedMessage
                  id="app.poaOverview.foreCast.missingIncome.CTA"
                  defaultMessage="Lägg till inkomst"
                />
              </Text>
            </Group>
          </UnstyledButton>
        ) : (
          withForecastInfo(
            expectedMonthlyPension === null ? (
              <Text size="sm" c="var(--wallet-color-neutrals-black)">
                <FormattedMessage
                  id="app.poaOverview.figures.errorForecast"
                  defaultMessage="Kan inte visas just nu"
                />
              </Text>
            ) : (
              <Value>
                <Sek value={expectedMonthlyPension} approx />
                {showAsterisk ? "*" : null}
              </Value>
            ),
          )
        )}
      </Figure>

      {guaranteedMonthlyPension !== null && (
        <Figure
          label={
            <FormattedMessage
              id="pensionOverview.summaryCard.notExposedToMarket"
              defaultMessage="Varav garanterad pension"
            />
          }
        >
          <Value>
            <Sek value={guaranteedMonthlyPension} approx />
          </Value>
        </Figure>
      )}

      {affectedByRiskPercent !== null && (
        <Figure
          label={
            <FormattedMessage
              id="pensionOverview.summaryCard.exposedToMarket"
              defaultMessage="Andel som påverkas av börsen"
            />
          }
        >
          <Group gap={6} wrap="nowrap">
            <Value>
              <NumberFormatter
                value={affectedByRiskPercent}
                decimalScale={0}
                suffix=" %"
              />
            </Value>
            {riskLegendGroups.length > 0 && (
              // The dots carry colour only, so the row states in words which groups it covers.
              <Group
                gap={4}
                wrap="nowrap"
                role="img"
                aria-label={intl.formatMessage(
                  {
                    id: "app.poaOverview.figures.riskLegend",
                    defaultMessage: "Gäller {groups}",
                  },
                  {
                    groups: riskLegendGroups
                      .map((group) =>
                        intl.formatMessage(RISK_DOT_MESSAGES[group]),
                      )
                      .join(", "),
                  },
                )}
              >
                {riskLegendGroups.map((group) => (
                  <span
                    key={group}
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: hatched(RISK_DOT_COLOUR[group]),
                    }}
                  />
                ))}
              </Group>
            )}
          </Group>
        </Figure>
      )}

      {expectedFeesUntilRetirement !== null && (
        <Figure
          label={
            <FormattedMessage
              id="app.poaOverview.figures.expectedFees"
              defaultMessage="Förväntade avgifter fram till pension"
            />
          }
        >
          {/* On a schablon the fee is an estimate too, which the same sheet explains. */}
          {withForecastInfo(
            <Value>
              <Sek value={expectedFeesUntilRetirement} approx />
            </Value>,
          )}
        </Figure>
      )}

      {footnote !== null && showAsterisk && (
        // Says pensionsbolag, not försäkringar: the backend can only count companies, so
        // naming insurances would be a false claim to the customer.
        <Text size="xs" c="var(--wallet-color-neutrals-gray-600)">
          <FormattedMessage
            id="app.poaOverview.figures.footnote"
            defaultMessage="*Baserat på svar från {answered} av {total} pensionsbolag."
            values={{ answered: footnote.answered, total: footnote.total }}
          />
        </Text>
      )}
    </Stack>
  );
}
